import { Auth, Hub } from "aws-amplify";
import { ClassExtended, ClassStudent, DbUser, GetClassRequest } from "./interfaces";
import TagManager from "react-gtm-module";
import axios from "./services/axios";
import axiosJwt from "./services/axios";
import { Class, TargetFeedbackDataResponse, UserQuizSubmission } from "@backend-types";
import { logger } from "./services/logger";
import { MacsAuthenticator } from "./hooks/useMacsAuthenticator";
import * as Sentry from "@sentry/react";

export const getJWT = async (): Promise<string> => {
  return await Auth.currentSession().then((res) => {
    let accessToken = res.getAccessToken();
    return accessToken.getJwtToken();
  });
};

/**
 * Remove duplicates in an array
 * @param arrArg array with dupes
 * @returns arr unique array
 */
export const uniqueArray = function (arrArg: any) {
  return arrArg.filter(function (elem: any, pos: number, arr: any) {
    return arr.indexOf(elem) === pos;
  });
};

function toDate(value: Date | string): Date {
  if (value instanceof Date) return value;
  // try ISO / RFC strings
  const iso = new Date(value);
  if (!Number.isNaN(iso.getTime())) return iso;
  // try numeric epoch-in-string
  const asNum = Number(value);
  if (!Number.isNaN(asNum)) {
    const numDate = new Date(asNum);
    if (!Number.isNaN(numDate.getTime())) return numDate;
  }
  // fallback (treat as oldest)
  return new Date(0);
}

export function isChmCourse(options: {
  courseName?: string;
  classSubject?: string;
  faqCourseTag?: string;
  progressDisplayType?: string;
}): boolean {
  const { courseName, classSubject, faqCourseTag, progressDisplayType } = options;

  if (courseName?.startsWith("CHM")) {
    return true;
  }

  if (classSubject?.startsWith("CHM")) {
    return true;
  }

  if (faqCourseTag?.startsWith("CHM")) {
    return true;
  }

  return progressDisplayType === "score";
}

export function removeQuizDupes(
  rows: UserQuizSubmission[] | undefined
): UserQuizSubmission[] {
  if (!rows?.length) return [];

  const bestByQuizId = new Map<string, UserQuizSubmission>();

  for (const row of rows) {
    const current = bestByQuizId.get(row.quiz_id);
    if (!current) {
      bestByQuizId.set(row.quiz_id, row);
      continue;
    }

    const curTs = toDate(current.created_at).getTime();
    const rowTs = toDate(row.created_at).getTime();

    if (rowTs > curTs || (rowTs === curTs && row.id > current.id)) {
      bestByQuizId.set(row.quiz_id, row);
    }
  }

  // Winners, but preserve original input order; also normalize created_at to Date in output.
  const winnerIds = new Set(Array.from(bestByQuizId.values()).map((r) => r.id));
  return rows
    .filter((r) => winnerIds.has(r.id))
    .map<UserQuizSubmission>((r) => ({ ...r, created_at: toDate(r.created_at) }));
}

export interface GtmDataLayer {
  event: string;
  action?: string;
  name?: string;
  type?: string;
  region?: string;
  section?: string;
  text?: string;
  component?: string;
  from?: string;
  to?: string;
}

export const ConvertToLowerCase = (obj: any) => {
  if (!obj) return obj;
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key.toLowerCase(),
      typeof value == "string" ? value.toLowerCase() : value,
    ])
  );
};

/**
 * Sleep for a given number of milliseconds
 * @param ms number of milliseconds to sleep
 */
export const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

interface GtmMetaData {
  [key: string]: string;
}

/**
 * Generate a Google Tag Manager data layer event
 * @param dataLayer Object containing the event properties
 * @param metaData Additonal data to be added to the dataLayer
 */
export const generateGtmEvent = (dataLayer: GtmDataLayer, metaData?: GtmMetaData) => {
  let dataLayerObj = { ...dataLayer, ...(metaData ? metaData : {}) };
  try {
    dataLayerObj = ConvertToLowerCase(dataLayerObj);
  } catch (e) {
    console.error(
      "Error occurred in converting datalayer object to lowercase",
      e,
      dataLayer,
      metaData
    );
  }
  console.log("dataLayerObj", dataLayerObj);
  TagManager.dataLayer({
    dataLayer: dataLayerObj,
  });
  resetDataLayer();
};
// Added to reset the data layer because in SPA data layer keeps on adding up
// ref: https://stackoverflow.com/questions/43673440/resetting-data-layer-variables-in-single-page-applications-google-tag-manager
// ref: https://developers.google.com/tag-platform/devguides/datalayer#reset
export const resetDataLayer = () => {
  // using window directly because gtm module which we use does not have reset method
  // @ts-ignore
  window.dataLayer.push(() => {
    // @ts-ignore
    window.google_tag_manager[
      process.env.REACT_APP_GTM_ID || "GTM-W334XCD"
    ].dataLayer.reset();
  });
};

export const getClasses = async (data: GetClassRequest, disablePagination?: boolean) => {
  try {
    const result = await axios.post("/classes", data, {
      params: {
        usermanagement: true,
        version: "v2",
        ...(disablePagination && { disablePagination: disablePagination }),
      },
    });
    return result.data.results;
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const getClass = async (classId: number) => {
  try {
    const result = await axios.get(`/classes/${Number(classId)}`);
    return result.data;
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const getClassesProgress = async (
  classes: ClassExtended[],
  moduleIds: string[]
) => {
  try {
    const classIds = classes.map((item) => {
      return { id: Number(item.id) };
    });
    const progressMap = new Map();

    for (const classId of classIds) {
      let response = await axios.post(`/insights/class-summary`, {
        type: "summary-progress",
        classes: [classId],
        courseFilter: {
          moduleIds: moduleIds,
        },
      });
      response.data?.forEach((item: any) =>
        progressMap.set(Number(item.id), item.percentage)
      );
    }

    return classes.map((item) => {
      return {
        ...item,
        percentage: progressMap.get(Number(item.id)) ?? 0,
      };
    });
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const getStudentsProgress = async (students: DbUser[], moduleIds: string[]) => {
  try {
    const studentIds = students.map((item) => {
      return { id: item.id };
    });
    let response = await axios.post(`/insights/student-summary`, {
      type: "summary-progress",
      students: studentIds,
      courseFilter: {
        moduleIds: moduleIds,
      },
    });
    const progressMap = new Map();
    response.data?.forEach((item: any) => progressMap.set(item.id, item.percentage));

    return students.map((item) => {
      return {
        ...item,
        percentage: progressMap.get(item.id) ?? 0,
      };
    });
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const getClassesFeedback = async (
  classes: ClassExtended[],
  moduleIds: string[]
) => {
  try {
    const classIds = classes.map((item) => {
      return Number(item.id);
    });

    let data = {
      classIds: classIds,
      courseFilter: {
        moduleIds: moduleIds,
      },
    };

    let response = await axios.post(`/insights/feedback-summary`, data);

    return response.data;
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const getClassesTargetFeedback = async (
  classes: ClassExtended[],
  targetId: string
): Promise<TargetFeedbackDataResponse> => {
  try {
    const classIds = classes.map((item) => {
      return Number(item.id);
    });

    let data = {
      classIds: classIds,
      courseFilter: {
        targetIds: [targetId],
      },
    };

    let response = await axios.post(`/insights/feedback-target`, data);

    return response.data;
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export type ClassListItem = Pick<Class, "id" | "subject" | "period_block" | "start_date">;
export interface AccessListItem {
  id: string;
  full_name: string;
  email: string;
  role: "Teacher" | "Observer";
  classDetails?: ClassListItem[];
}
type GetObserverResponse = {
  observers: AccessListItem[];
  teachers: AccessListItem[];
  studentsClasses: ClassListItem[];
};
export const getObservers = async (data: any) => {
  try {
    const result = await axios.get<GetObserverResponse>(`/observers?${data}`);
    // return result.data.map((item: any) => {
    //   return {
    //     id: item.id,
    //     full_name: item.full_name,
    //     email: item.email,
    //     role: "Observer",
    //   };
    // });
    return result.data;
  } catch (err) {
    throw err;
  }
};

export const addObservers = async (data: any, observerList: any[]) => {
  try {
    return await Promise.allSettled(
      observerList.map(async (observer) => {
        return new Promise(async (resolve, reject) => {
          try {
            let response = await axios.post("/observers", {
              ...data,
              ...{ grantToId: observer.id },
            });
            resolve(response.data);
          } catch (e: any) {
            reject(`Adding ${observer.full_name} as observer failed ${e.response.data}`);
          }
        });
      })
    );
  } catch (e) {
    throw e;
  }
};

export const deleteObserver = async (data: any) => {
  return new Promise(async (resolve, reject) => {
    try {
      let response = await axios.delete(`/observers`, { data });
      resolve(response.data);
    } catch (e: any) {
      reject(`Removing access failed ${e.response.data}`);
    }
  });
};

export function subtractMonths(date: Date, months: number) {
  date.setMonth(date.getMonth() - months);
  return date;
}

export function subtractDays(date: number, days: number) {
  return new Date(date - days * 24 * 60 * 60 * 1000);
}

export function formatDate(date: Date) {
  const month = date.getMonth() + 1;
  return (
    (month > 9 ? month : "0" + month) +
    "/" +
    (date.getDate() > 9 ? date.getDate() : "0" + date.getDate()) +
    "/" +
    date.getFullYear()
  );
}

export function addDate(date: Date, days: number) {
  return new Date(date.getTime() + days * 24 * 60 * 60 * 1000);
}

export function timeDifference(current: any, previous: any) {
  var msPerMinute = 60 * 1000;
  var msPerHour = msPerMinute * 60;
  var msPerDay = msPerHour * 24;
  var msPerWeek = msPerDay * 7;
  var msPerYear = msPerDay * 365;

  var elapsed = current - previous;

  if (elapsed < msPerMinute) {
    return Math.round(elapsed / 1000) + "s ago";
  } else if (elapsed < msPerHour) {
    return Math.round(elapsed / msPerMinute) + "m ago";
  } else if (elapsed < msPerDay) {
    return Math.round(elapsed / msPerHour) + "h ago";
  } else if (elapsed < msPerWeek) {
    return Math.round(elapsed / msPerDay) + "d ago";
  } else if (elapsed < msPerYear) {
    return Math.round(elapsed / msPerWeek) + "w ago";
  } else {
    return Math.round(elapsed / msPerYear) + "y ago";
  }
}

export const GetNotifications = async (page: number) => {
  try {
    let response = await axios.get(`/notifications?page=${page}`);
    return response.data;
  } catch (e) {
    logger.error(e);
    throw e;
  }
};

export const ReSendLoginCredentialsToUser = async (email: string) => {
  try {
    return await axios.post(`/cognito-users-verify`, { email });
  } catch (e: any) {
    if (e.response.status === 400) {
      // user is already verified so backend server gives code 400
      throw e;
    }
    if (e.response.status === 404) {
      throw new Error(`User with email ${email} not found`);
    }
    logger.error(e);
  }
};

export const FetchClassStudents = async (classId: number) => {
  try {
    let response = await axios.get(`/insights/class/${classId}`);
    return response.data;
  } catch (e) {
    logger.error(e);
    throw e;
  }
};

export const ReSendLoginCredentialsToClass = async (classId: number) => {
  let users = [];
  try {
    users = await FetchClassStudents(classId);
  } catch (e: any) {
    logger.error(e);
    if (e.response.status !== 500 || e.code !== "ERR_NETWORK") {
      throw e;
    }
  }
  let failedUsers = [];
  for (const user of users) {
    if (user.last_access_at === null) {
      try {
        await ReSendLoginCredentialsToUser(user.email);
      } catch (e) {
        failedUsers.push(user);
        logger.error(e);
      }
    }
  }
  if (failedUsers.length > 0) {
    return {
      status: false,
      message: `Failed to send login credentials to ${failedUsers.join(", ")}`,
      failedUsers,
    };
  }
  return {
    status: true,
    message: `Login credentials sent successfully!`,
    failedUsers,
  };
};

export const LogoutUserLti = async () => {
  try {
    let res = await axiosJwt.get("/logout");
    console.log(res);
    console.log(res.status);
    if (res.status === 200) {
      sessionStorage.clear();
      return res.data;
    }
  } catch (e) {
    console.log(e);
  }
};

export const GetUserInfo = async (userId: string) => {
  try {
    const result = await axios.get(`/user/${userId}`);
    return result.data;
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const HandleDataLayer = (
  event: string,
  action: string,
  type: string,
  region: string,
  section: string,
  text: string,
  name?: string,
  component?: string,
  metadata?: any
) => {
  generateGtmEvent(
    {
      name: name ? name : "onclick",
      event: event,
      action: action,
      type: type,
      region: region,
      section: section,
      text: text,
      component: component,
    },
    metadata
  );
};

export const HandleUserLogout = async (opts?: {
  clearCognito?: boolean;
  clearCookies?: boolean;
  clearSession?: boolean;
  clearLocal?: boolean;
  redirectToSessionEnd?: boolean;
  redirectToLogin?: boolean;
}) => {
  try {
    const currentUrl = new URL(window.location.href);

    if (opts?.clearLocal) localStorage.clear();
    if (opts?.clearSession) sessionStorage.clear();

    if (opts?.clearCognito) {
      await Auth.signOut();
      Hub.dispatch("UI Auth", {
        event: "AuthStateChange",
        message: "signedout",
      });
    }

    if (opts?.clearCookies) {
      const data = await LogoutUserLti();
      console.log("Logging out user: ", data);
    }

    MacsAuthenticator.authHook.setAuthenticated(false);
    MacsAuthenticator.authHook.setAuthType(null);
    MacsAuthenticator.authHook.setInitialized(false);
    MacsAuthenticator.authHook.setLtik(null);
    Sentry.setUser(null);

    if (opts?.redirectToSessionEnd) {
      const redirectUrl = new URL("/session-ended", currentUrl);
      redirectUrl.search = "";
      window.location.href = redirectUrl.toString();
    } else if (opts?.redirectToLogin) {
      const redirectUrl = new URL("/", currentUrl);
      redirectUrl.search = "";
      window.location.href = redirectUrl.toString();
    }
  } catch (error) {
    console.error(error); // Using console for the sake of this example, replace with your logger if needed
  }
};

export const getNumericDayFromDate = (date: Date) => {
  const timeZone = "GMT";

  // Create a new Date object with the current date and time in the specified time zone
  const options: Intl.DateTimeFormatOptions = { timeZone, weekday: "short" };

  // Format the date and time in the desired time zone
  const dateFormatter = new Intl.DateTimeFormat("en-US", options);
  const formattedDate = dateFormatter.formatToParts(date);

  // Extract the day as a number from the formatted date
  const dayString = formattedDate.find((part) => part.type === "weekday")?.value;
  return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(dayString ?? "Sun");
};

export const merge = (a: any[], b: any[], predicate = (a: any, b: any) => a === b) => {
  const c = [...a]; // copy to avoid side effects
  // add all items from B to copy C if they're not already present
  b.forEach((bItem) =>
    c.some((cItem) => predicate(bItem, cItem)) ? null : c.push(bItem)
  );
  return c;
};

export const sanitizeData = (data: Record<string, any>) => {
  const sanitizedData: Record<string, any> = {};
  for (const key in data) {
    if (data.hasOwnProperty(key)) {
      const value = data[key];
      if (value === null || value === undefined || Number.isNaN(value)) {
        sanitizedData[key] = "";
      } else {
        sanitizedData[key] = value;
      }
    }
  }
  return sanitizedData;
};

export const SortAndFilterStudentClasses = (classStudentList: ClassStudent[]) => {
  return classStudentList
    .map((item) => item.class!)
    .sort((a, b) => {
      const dateA = a?.start_date ? new Date(a.start_date).getTime() : -Infinity;
      const dateB = b?.start_date ? new Date(b.start_date).getTime() : -Infinity;

      return dateA - dateB;
    })
    .filter(
      (item) =>
        !item?.end_date || new Date(item?.end_date).getTime() > new Date().getTime()
    );
};

export function scrubHTML(input: string, allowedTags: string[]): string {
  // Create a temporary DOM element to use the browser's built-in functionality
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = input;

  // Function to recursively remove all tags except the allowed ones
  function traverse(node: ChildNode): void {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      // If it's not in the allowed tags list, remove it
      if (!allowedTags.includes(element.tagName.toLowerCase())) {
        const childNodesArray = Array.from(element.childNodes);
        element.replaceWith(...childNodesArray); // Convert to array for replacement
      }
      // Recursively traverse the child nodes using a standard for loop
      const childNodes = element.childNodes;
      for (let i = 0; i < childNodes.length; i++) {
        traverse(childNodes[i]);
      }
    }
  }

  // Start the traversal from the root node using a standard for loop
  const childNodes = tempDiv.childNodes;
  for (let i = 0; i < childNodes.length; i++) {
    traverse(childNodes[i]);
  }

  return tempDiv.innerHTML;
}

export function getSupportLink(higherEdCourse?: boolean): string {
  const now = new Date();

  // Get Arizona time
  const azTime = new Date(now.toLocaleString("en-US", { timeZone: "America/Phoenix" }));
  const hour = azTime.getHours(); // 0–23
  const day = azTime.getDay(); // 0 = Sunday, 6 = Saturday

  // Determine if outside business hours for higher ed courses
  const isOutsideBusinessHours =
    // higherEdCourse &&
    // Weekends: Friday 5:00 PM to Monday 7:00 AM
    (day === 5 && hour >= 17) || // Friday after 5:00 PM
    day === 6 || // Saturday
    day === 0 || // Sunday
    (day === 1 && hour < 7) || // Monday before 7:00 AM
    // Weekdays (Mon–Fri) 5:00 PM to 7:00 AM
    (day >= 1 && day <= 5 && (hour < 7 || hour >= 17));

  // Links
  const ecCenterLink = "https://my.asu.edu/service";
  const jsmLink = higherEdCourse
    ? "https://asudev.jira.com/servicedesk/customer/portal/31/group/72/create/296"
    : "https://asudev.jira.com/servicedesk/customer/portal/31";

  return isOutsideBusinessHours ? ecCenterLink : jsmLink;
}

export function isChrome(): boolean {
  if (typeof navigator === "undefined") return false;

  const ua = navigator.userAgent;

  return (
    ua.includes("Chrome") || // Desktop Chrome, Android Chrome
    ua.includes("CriOS") // Chrome on iOS
  );
}

export function isSafari(): boolean {
  if (typeof navigator === "undefined") return false;

  const ua = navigator.userAgent;

  return (
    ua.includes("Safari") &&
    !ua.includes("Chrome") &&
    !ua.includes("Chromium") &&
    !ua.includes("Edg") &&
    !ua.includes("CriOS") && // Chrome iOS
    !ua.includes("FxiOS") // Firefox iOS
  );
}

export type BrowserName = "Chrome" | "Edge" | "Firefox" | "Safari" | "Opera" | "Other";

export function getBrowserName(): BrowserName {
  if (typeof navigator === "undefined") return "Other";

  const ua = navigator.userAgent;
  // Order matters because modern browser user agents overlap (e.g., Edge and Opera include "Chrome", Chrome includes "Safari").
  // We check the most specific identifiers first to prevent false positives from broader matches.
  if (/Edg\//.test(ua)) return "Edge";
  if (/OPR\//.test(ua)) return "Opera";
  if (/Chrome\//.test(ua)) return "Chrome";
  if (/Firefox\//.test(ua)) return "Firefox";
  if (/Safari\//.test(ua)) return "Safari";

  return "Other";
}

export type DeviceLabel =
  | "windows_desktop"
  | "mac_desktop"
  | "linux_desktop"
  | "iphone"
  | "android_phone"
  | "tablet"
  | "other";

export function getDeviceLabel(): DeviceLabel {
  if (typeof navigator === "undefined") return "other";

  const ua = navigator.userAgent;

  // Tablets first (order matters)
  if (/iPad|Tablet/.test(ua)) return "tablet";

  // Phones
  if (/iPhone/.test(ua)) return "iphone";
  if (/Android.*Mobi/.test(ua)) return "android_phone";

  // Desktops
  if (/Windows/.test(ua)) return "windows_desktop";
  if (/Mac OS/.test(ua)) return "mac_desktop";
  if (/Linux/.test(ua)) return "linux_desktop";

  return "other";
}
